/**
 * CleanupCelebration — the small reward on a duplicate-LinkedIn card whose
 * collision is actually gone.
 *
 * IT REWARDS THE OUTCOME, NOT THE CLICK, and that is the whole design. Trello
 * #790 Item 2 parks the question — *does it reward the outcome or the click?* —
 * and records that outcome-rewards age far better. So this component is
 * rendered from the RE-CHECK RESPONSE's `cleared` branch and from nothing else:
 * a celebration wired to the option handler would be a participation trophy, it
 * would be dishonest the first time somebody misremembered fixing Thrive, and
 * it is exactly what #790 warns "feels condescending". The caller owns that
 * wiring; this component only knows how to look pleased.
 *
 * IT SHIPS ON since 2026-09-15. `?gamification=off` (see `CompareGamificationProvider`)
 * is what HIDES it now; #790 wanted Jared's eyes on the execution before
 * it lands for recruiters. The verification behaviour underneath — the Thrive
 * read, the shake, the toast — ships regardless.
 *
 * THE SEQUENCE (operator, 2026-09-10). It was three small dots and a label,
 * sized down on the reasoning that a card in a scrolling queue should not
 * shout. Watching it run, the operator asked for the opposite: a burst of paper
 * and a big "Well done", after which the ROW ITSELF retires from the review
 * list. This is the only point in the flow that acknowledges the recruiter's
 * own trip to Thrive, and it was the quietest thing on the screen.
 *
 * IT DOES NOT ADD ANYONE TO A SEARCH. The row leaving the list is presentation
 * only — the real add-to-search on a resolved conflict is deliberately out of
 * scope (operator, 2026-09-10). Nothing here writes to Thrive, and a future
 * reader must not take the disappearance as evidence that something did.
 *
 * REDUCED MOTION IS GUARDED HERE, not in `index.css`, because none of this
 * passes through a stylesheet the app owns: the burst is a canvas library
 * (which gets `disableForReducedMotion` as well) and the entrance is the
 * animation API. The preference costs the SHOW and never the OUTCOME — the line
 * still renders and the row still retires.
 */

import confetti from "canvas-confetti";
import { useEffect, useRef, useState } from "react";

import { RECHECK_CELEBRATION_LINE } from "./conflictActionsCopy";

/** Read `prefers-reduced-motion`, tolerant of envs without `matchMedia`
 *  (happy-dom does not implement it by default). Subscribes, because a reader
 *  can flip the OS setting while the modal is open. Mirrors the shape
 *  `@nu-assist/ui`'s `DockablePanel` already uses. */
export function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
    try {
      return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    } catch {
      return false;
    }
  });

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    let mq: MediaQueryList;
    try {
      mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    } catch {
      return;
    }
    const onChange = () => setReduced(mq.matches);
    // `addEventListener` on a MediaQueryList is the modern form; the legacy
    // `addListener` fallback is what older Safari and some test doubles expose.
    if (typeof mq.addEventListener === "function") {
      mq.addEventListener("change", onChange);
      return () => mq.removeEventListener("change", onChange);
    }
    if (typeof mq.addListener === "function") {
      mq.addListener(onChange);
      return () => mq.removeListener(onChange);
    }
    return;
  }, []);

  return reduced;
}

export interface CleanupCelebrationProps {
  /** Testid suffix so a card with several of these could still be addressed. */
  testId?: string;
  /** What the big line says. A PROP because the two things worth celebrating
   *  are different facts: a collision verified gone, and a contact actually
   *  written into the search. This component still knows neither — it is handed
   *  the sentence, exactly as it is handed the moment. */
  line?: string;
  /**
   * Whether to throw paper. Default true.
   *
   * THE RETIREMENT IS NOT OPTIONAL — only the show is. Without the gamification
   * flag the caller used to render a plain `<output>` of its own and the row
   * announced its result and then sat there forever (operator, 2026-09-11: "el
   * same person no desaparece del listado"). Routing both cases through this
   * component keeps one line and one shape; the retirement itself belongs to
   * the caller, which uses `celebrationDurationMs` — this flag is one of its
   * inputs, since a skipped show is over almost at once.
   */
  burst?: boolean;
  /**
   * HOW MUCH work this fix represents — the number of duplicate Thrive records
   * the recruiter cleaned up to get here.
   *
   * N BURSTS, ONE AFTER THE OTHER — "lanza un efecto, termina, sale otro,
   * termina" (operator, 2026-09-12). Three records cleaned is three
   * celebrations in sequence, and only then does the row leave.
   *
   * The first cut made ONE bigger burst instead, on the theory that a series
   * would read as a stutter. It does not read as anything: "no me di cuenta que
   * la de Maria era una fiesta más grande, se ven todas iguales". Amplitude is
   * invisible without something beside it to compare against; COUNTING is not.
   */
  magnitude?: number;
}

/** Where the paper lands, in the design system's own colours — a burst in
 *  arbitrary party colours reads as a different product's toast. */
const CONFETTI_COLORS = ["#16a34a", "#22c55e", "#86efac", "#fbbf24", "#60a5fa"];

/** How long the whole thing takes. The row is still on screen for all of it,
 *  which is the point: the recruiter has to SEE which row was cleared. */
const SEQUENCE_MS = 1400;

/** Gap between rounds — long enough that each burst reads as its own event
 *  rather than as one continuous shower. */
const ROUND_MS = 700;

/** Most rounds we will ever fire. A pile of thirty is a reward, not a wait. */
const MAX_ROUNDS = 4;

/** How many bursts a given magnitude earns. */
export function celebrationRounds(magnitude: number): number {
  return Math.min(Math.max(Math.round(magnitude), 1), MAX_ROUNDS);
}

/**
 * HOW LONG THE ROW MUST STAY ON SCREEN, and the ONLY place that answers it.
 *
 * The caller owns the retirement — a row's disappearance must not depend on a
 * child's timer surviving StrictMode's double mount, which is a bug this
 * component already shipped once. But the caller cannot own the DURATION: that
 * is a fact about the series this component fires, and the modal had it as a
 * hardcoded `2400`, calibrated when every celebration was a single burst.
 *
 * A three-round series ends at 2800ms and a four-round one at 3500ms, so the
 * two biggest celebrations — the ones a recruiter earns by cleaning up the
 * worst pile-ups — were exactly the ones whose last burst got cut off
 * (/pr-review iter 1, agent A). Export the arithmetic instead of restating it.
 */
export function celebrationDurationMs(
  magnitude: number,
  opts: { burst?: boolean; reducedMotion?: boolean } = {}
): number {
  // No show, no series: just long enough to read the line.
  if (opts.reducedMotion || opts.burst === false) return 400;
  return SEQUENCE_MS + (celebrationRounds(magnitude) - 1) * ROUND_MS;
}

export function CleanupCelebration({
  testId = "cleanup-celebration",
  line = RECHECK_CELEBRATION_LINE,
  burst = true,
  magnitude = 1,
}: CleanupCelebrationProps) {
  const reducedMotion = usePrefersReducedMotion();
  const hostRef = useRef<HTMLOutputElement | null>(null);
  // WHAT STOPS THE SHOW REPLAYING IS THE DEPENDENCY LIST, and it took three
  // bugs to establish that it is also all that should.
  //
  // The operator watched paper fall "hasta el infinito" (2026-09-11): the
  // caller passed a completion callback as an inline arrow, so its identity
  // changed on every render, the effect re-ran, fired another burst, called it,
  // and went round. That callback is gone entirely now — the caller owns the
  // retirement — so nothing in the props changes identity per render.
  //
  // A once-per-mount flag was added on top of that, and it caused both of the
  // bugs that followed, because `StrictMode` mounts, unmounts and mounts again.
  // Covering the burst AND the timer, it left the paper flying and the row on
  // screen ("disparo la fiesta, pero quedo en la ui"). Narrowed to the burst
  // alone, it swallowed every round after the first, since the cleanup had just
  // cancelled them and the flag blocked the re-arm ("Maria sigue sin dar
  // multiple fiesta" — three sessions).
  //
  // So there is no flag. The effect keys on the three values that define the
  // show, the series is re-armed on every mount, and the cleanup cancels
  // everything the previous mount scheduled.

  /** Pending round timers, cleared on unmount so a retired row cannot keep
   *  throwing paper at whatever takes its place. */
  const timersRef = useRef<number[]>([]);

  useEffect(() => {
    // REDUCED MOTION AND `burst:false` SKIP THE SHOW, NOT THE OUTCOME.
    if (!reducedMotion && burst) {
      const host = hostRef.current;
      const rounds = Math.min(Math.max(Math.round(magnitude), 1), 4);
      if (host) {
        const r = host.getBoundingClientRect();
        const origin = {
          x: (r.left + r.width / 2) / window.innerWidth,
          y: (r.top + r.height / 2) / window.innerHeight,
        };
        // ONE PER ROUND, spaced so each lands before the next starts. Fired
        // from the ELEMENT'S OWN position: a page-centred burst on a queue of
        // thirty cards congratulates nothing in particular.
        //
        // EVERY ROUND GOES THROUGH A TIMER, INCLUDING THE FIRST, and the first
        // one's zero delay is load-bearing. Firing round 0 synchronously and
        // timing the rest looked equivalent and was not: StrictMode mounts,
        // unmounts and mounts again, so the sync burst landed, the cleanup
        // cancelled rounds 1..N, and the second mount — blocked by the
        // once-guard this loop used to sit behind — re-armed nothing. Four
        // cleaned records produced exactly one burst, in the only environment
        // the app actually runs in (operator, across three sessions: "Maria
        // sigue sin dar multiple fiesta"). A zero-delay timer is cancelled by
        // that same cleanup before it can run, so the second mount replays the
        // whole series instead of the tail of it.
        //
        // Nothing guards re-entry any more, and nothing needs to: the effect's
        // deps are the three values that DEFINE the show, and there is no
        // callback prop left whose per-render identity could re-arm it.
        for (let i = 0; i < rounds; i++) {
          timersRef.current.push(
            window.setTimeout(
              () =>
                void confetti({
                  particleCount: 60,
                  spread: 70,
                  startVelocity: 28,
                  gravity: 1.1,
                  scalar: 0.8,
                  ticks: 120,
                  colors: CONFETTI_COLORS,
                  disableForReducedMotion: true,
                  origin,
                }),
              i * ROUND_MS
            )
          );
        }
        host.animate?.(
          [
            { transform: "scale(0.94)", opacity: 0 },
            { transform: "scale(1.02)", opacity: 1, offset: 0.35 },
            { transform: "scale(1)", opacity: 1 },
          ],
          { duration: 420, easing: "cubic-bezier(0.2, 0.8, 0.2, 1)" }
        );
      }
    }

    // NO RETIREMENT TIMER HERE. It was removed with the `onDone` prop it
    // called: production never passed one (`CompareToThriveModal` renders this
    // component without it and owns the retirement itself), so the timer,
    // the prop, the ref and nine tests all exercised a path no recruiter ever
    // reached — while this file's own docstring described it as the mechanism.
    // Found in /pr-review iter 1 by two agents; the same class of dead
    // contract this epic already paid for once.
    //
    // What the caller cannot compute for itself is HOW LONG the series takes,
    // and that is now exported as `celebrationDurationMs` rather than restated
    // as a constant three files away.
    return () => {
      for (const id of timersRef.current) clearTimeout(id);
      timersRef.current = [];
    };
  }, [reducedMotion, burst, magnitude]);

  return (
    // `<output>`, whose implicit ARIA role IS `status` — a polite live region,
    // not `alert`: this is good news arriving beside a line the reader is
    // already being given, not an interruption. The native element rather than
    // `<div role="status">` so the semantics come from the tag.
    <output
      ref={hostRef}
      data-testid={testId}
      data-reduced-motion={reducedMotion ? "true" : "false"}
      className={
        burst
          ? "flex items-center justify-center gap-2 rounded-md border border-success bg-success-muted px-3 py-2"
          : "rounded-md border border-success bg-surface px-2.5 py-1.5 text-label text-success-ink"
      }
    >
      {/* The line is BIG now (operator, 2026-09-10). It used to be a label
          beside three dots — deliberately small, on the reasoning that a card
          in a scrolling queue should not shout. The operator watched it and
          asked for the opposite: this is the moment the recruiter's own trip to
          Thrive paid off, and it is the only place in the flow that says so. */}
      {burst && (
        <span aria-hidden="true" className="text-h3">
          🎉
        </span>
      )}
      <span className={burst ? "text-h3 font-semibold text-success-ink" : undefined}>{line}</span>
    </output>
  );
}
